<template lang="pug">
div
  //- Fetching
  template(v-if="isFetching && !initialized")
    div
      v-skeleton-loader(type="table-heading")
      v-skeleton-loader.mt-3(type="text@3")
  template(v-else-if="isError")
    div.height-adjustment.d-flex.align-center.justify-center.flex-column
      v-icon.mb-2(size="30" color="danger500") mdi-alert-circle-outline
      .text-sm.danger500--text Opss.. Unable to get family relationship
      .text-sm.danger500--text Please try again later
  template(v-else)
    v-card.round-xl.mt-2(outlined)
      v-simple-table.table-custom(dense)
        thead
          tr
            th.text-xs.font-medium.neutral500--text(style="text-transform: none !important;") Patient
            th.text-xs.font-medium.neutral500--text(style="text-transform: none !important;") Relationship
            th.text-xs.font-medium.neutral500--text(style="text-transform: none !important;" width="80")
        tbody
          template(v-for="(item, idx) in existingRelationships")
            tr(:key="`alg-${idx}`" style="height: 50px;")
              td
                div.text-xs.font-medium.neutral700--text {{ item.related_person.name }}
              td
                div.text-xs.font-medium.neutral700--text {{ item.relationship.name }}
              td.text-xs.neutral700--text.text-end.pr-4.pl-0
                y-button.mr-2(outlined color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-trash-03" icon-color="danger500" icon-size="16" unset-min-width size="xxs" height="24" button-class="px-1" @click="handleToDelete(item)")
                y-button(outlined color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-edit-02" icon-color="neutral700" icon-size="16" unset-min-width size="xxs" height="24" button-class="px-1" @click="handleToEditItem(item)")
    y-button.mt-4(label="Add more" outlined color="neutral400" prepend-icon="$yzi-plus" icon-size="16" icon-color="neutral500" text-color="neutral700" size="xs" height="28" unset-min-width button-class="px-2" @click="showAddEditFamily = true")
  .dialogs
    add-edit-family-dialog(v-if="showAddEditFamily" :open.sync="showAddEditFamily" :patientId="patientId" :patientName="patientName" :parentItem="toEditItem" @saved="handleAddNew" @closed="toEditItem = null")
    confirm-delete-relationship-dialog(v-if="showConfirmDeleteDialog" :open.sync="showConfirmDeleteDialog" :toDeleteItem="toDeleteItem" @delete="handleConfirmDelete")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddEditFamilyDialog from "@/components/clinic/relationship/AddEditFamilyDialog.vue"
import ConfirmDeleteRelationshipDialog from "@/components/clinic/relationship/ConfirmDeleteRelationshipDialog.vue"

export default {
  components: {
    AddEditFamilyDialog, ConfirmDeleteRelationshipDialog
  },
  props: {
    patientId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isFetching: false,
      initialized: false,
      isError: false,
      showAddEditFamily: false,
      showConfirmDeleteDialog: false,
      patient: null,
      relationshipOptions: [],
      existingRelationships: [],
      headers: [
        { text: 'Name', value: 'related_person.name' },
        { text: 'Relationship', value: 'relationship.name' },
        { text: 'Action', value: 'action', align: 'end' },
      ],
      toDeleteItem: null,
      toEditItem: null
    }
  },
  async mounted () {
    try {
      this.isFetching = true
      const allFetching = [this.fetchRelationshipOptions(), this.fetchPatientRelationships(), this.fetchPatient()]
      await Promise.all([...allFetching])
      this.initialized = true
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      this.isError = true
    } finally {
      this.isFetching = false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    patientName () {
      return this.patient?.name
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    fetchPatient () {
        return this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patientId}`)
          .then(({ data }) => this.patient = data)
    },
    fetchRelationshipOptions () {
      return this.$axios.get('/clinics/relationship_options')
        .then(({ data }) => this.relationshipOptions = data.results)
    },
    fetchPatientRelationships () {
      const params = {
        expand: 'relationship,related_person',
        patient: this.patientId
      }
      return this.$axios.get(`/clinics/${this.company.slug}/patient_relationships`, { params })
        .then(({ data }) => this.existingRelationships = data.results)
    },
    async handleAddNew () {
      try {
        this.isFetching = true
        await this.fetchPatientRelationships()
        this.$emit('updated')
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    handleToDelete (item) {
      this.toDeleteItem = item
      this.showConfirmDeleteDialog = true
    },
    async handleConfirmDelete () {
      try {
        const endpoint = `/clinics/${this.company.slug}/patient_relationships/${this.toDeleteItem.id}`
        const resp = await this.$axios.delete(endpoint)
        await this.fetchPatientRelationships()
        this.showMessage(['success', 'Relationship deleted'])
        this.showConfirmDeleteDialog = false
        this.$emit('updated')
        this.updateActivityLog(endpoint, null, resp, 'delete', this.toDeleteItem.id)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    handleToEditItem (item) {
      this.toEditItem = {...item}
      this.showAddEditFamily = true
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'family_relationship',
        activity: `${this.$options.filters.capitalize(method)} family relationship (#${id})`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.height-adjustment {
  height: 70dvh;
}
</style>  